// VERSIONS tab — the real pane (F-VERSIONS, slice A). Composes the snapshot
// history list (pure BE-consumption via `useVersions`) + the in-pane read-only
// version preview (F-VERSIONS-PREVIEW, via `useEcriturePreview`) + an honest
// ComingSoon for snapshot-create / restore (Office.js-deferred). Mirrors
// `SourcesPane`'s load-on-active-brief posture and `PlanPane`'s compose posture.
//
// Invariants:
//   W1 — no business logic. The history derivation (filter `snapshotOf ===
//        briefId` + sort desc) + the écriture detail projection live in the
//        adapter; the pane only orchestrates list↔preview state.
//   W2 — no Office.js. Snapshot-create / restore are ComingSoon precisely
//        because they need a `Word.run` capture/write adapter (deferred). The
//        preview is the explicitly NON-Office.js half (pure BE consumption).
//   W5 — no new store. Reuses `useCaseStore` + local hook state for the
//        ephemeral preview (per-preview UI state — no cross-component subscribe).
//   W6 — no $/tokens. Rows show label/author/date only.
//   §9 — logical RTL utilities; BE snapshot label/author + section text get
//        `bidiProps` (inside VersionHistoryList / SectionRenderer).

import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useVersions } from '@/ui/hooks/use-versions'
import { useEcriturePreview } from '@/ui/hooks/use-ecriture-preview'
import { useCaseStore } from '@/ui/stores/use-case-store'
import { VersionHistoryList } from './VersionHistoryList'
import { VersionPreview } from './VersionPreview'
import { SnapshotComingSoon } from './SnapshotComingSoon'

export function VersionsPane(): JSX.Element {
  const { t } = useTranslation()
  const { snapshots, loading, error, load } = useVersions()
  const preview = useEcriturePreview()
  const activeBriefId = useCaseStore((s) => s.activeContext?.briefId ?? null)

  // Load the active brief's snapshot history when a brief becomes active.
  // Switching brief also closes any open preview (it belongs to the old brief).
  useEffect(() => {
    preview.close()
    if (activeBriefId) void load()
    // `load` + `preview.close` are stable useCallback refs; keying on the
    // active brief is the intended trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBriefId])

  // ── Gate: no active brief → honest empty hint (mirror BriefRoster) ────────
  if (activeBriefId == null) {
    return (
      <div data-testid="versions-pane" className="flex flex-1 flex-col">
        <div
          data-testid="versions-empty-no-brief"
          className="border-b border-ink/10 px-3.5 py-4"
        >
          <div className="text-[10px] font-bold uppercase tracking-wider text-ink/40">
            {t('versions.header')}
          </div>
          <p className="mt-2 text-xs leading-relaxed text-ink/60">
            {t('versions.noBriefHint')}
          </p>
        </div>
        <SnapshotComingSoon />
      </div>
    )
  }

  // A preview is "active" the moment a row is clicked — through its loading /
  // error / detail lifecycle. While active it replaces the history list.
  const previewActive =
    preview.loading || preview.error != null || preview.detail != null

  if (previewActive) {
    return (
      <div data-testid="versions-pane" className="flex flex-1 flex-col">
        <VersionPreview
          detail={preview.detail}
          loading={preview.loading}
          error={preview.error}
          onClose={preview.close}
        />
        <SnapshotComingSoon />
      </div>
    )
  }

  return (
    <div data-testid="versions-pane" className="flex flex-1 flex-col">
      <div className="border-b border-ink/10 px-3.5 py-3">
        <div className="text-[10px] font-bold uppercase tracking-wider text-ink/40">
          {t('versions.header')}
        </div>
        <p className="mt-1 text-[11px] leading-relaxed text-ink/60">
          {t('versions.help')}
        </p>
      </div>

      <div className="flex flex-1 flex-col overflow-y-auto">
        {loading && (
          <div
            data-testid="versions-loading"
            className="px-3.5 py-3 text-xs text-ink/60"
          >
            {t('versions.loading')}
          </div>
        )}

        {error && (
          <div
            role="alert"
            data-testid="versions-error"
            className="m-2 rounded-md border-s-2 border-warn bg-warn/5 px-2.5 py-1.5 text-[11px] text-ink/80"
          >
            {error.message}
          </div>
        )}

        {!loading && !error && (
          <VersionHistoryList snapshots={snapshots} onView={preview.open} />
        )}
      </div>

      <SnapshotComingSoon />
    </div>
  )
}
